import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { inputCls } from "@/lib/catalog";

// Category defaults: each Type -> ONE supplier product line (brand/model text on a line doesn't matter).
export function CategoryDefaults({ version }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/settings/category-defaults").then((r) => setRows(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, [version]);
  const pick = async (r, v) => {
    try {
      await api.put(`/settings/category-defaults/${r.type_id}`, { line_id: v === "" ? null : v });
      toast.success(`${r.type_name}: default updated`);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const pickScreen = async (r, v) => {
    try {
      await api.put(`/settings/category-defaults/${r.type_id}/screen`, { screen_id: v || null });
      toast.success(`${r.type_name}: default screen updated`);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  if (!rows) return null;
  return (
    <div data-testid="category-defaults" className="mt-6">
      <h3 className="font-display text-sm font-bold text-slate-100">Category defaults</h3>
      <p className="mb-2 text-xs text-slate-400">Every line of a type is priced with its default product line, whatever brand or model it names. A named series (e.g. AW150) or the line picker overrides it.</p>
      <table className="w-full text-left text-sm">
        <thead className="text-[11px] uppercase tracking-wide text-slate-500">
          <tr><th className="px-2 py-1">Type</th><th className="px-2">Category</th><th className="px-2 text-right">Lines</th><th className="px-2">Default product line</th><th className="px-2">Default screen</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.type_id} data-testid={`category-default-row-${i}`} className="border-t border-slate-800/70">
              <td className="px-2 py-1.5 text-slate-100">{r.type_name}</td>
              <td className="px-2 text-slate-400">{r.category_label || "—"}</td>
              <td className="px-2 text-right font-mono-num text-slate-400">{r.lines.length}</td>
              <td className="px-2">
                {r.lines.length === 0 ? (
                  <span data-testid={`category-default-none-${i}`} className="text-xs font-semibold text-red-500">No supplier product</span>
                ) : (
                  <select data-testid={`category-default-select-${i}`} value={r.choice === "none" ? "none" : r.choice ? r.default_line_id || "" : ""} onChange={(e) => pick(r, e.target.value)} className={inputCls}>
                    <option value="">Automatic{!r.choice ? ` · ${r.lines.find((l) => l.id === r.default_line_id)?.name || ""}` : " (first of category)"}</option>
                    {r.lines.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    <option value="none">No supplier product</option>
                  </select>
                )}
              </td>
              <td className="px-2">
                {r.screens?.length ? (
                  <select data-testid={`category-default-screen-select-${i}`} value={r.default_screen_id || ""} onChange={(e) => pickScreen(r, e.target.value)} className={inputCls}>
                    <option value="">None</option>
                    {r.screens.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                ) : <span className="text-xs text-slate-600">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
